import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Calendar, MessageSquare, Heart, Bookmark, Ban,
  Crown, Trophy, Star,
} from "lucide-react";
import api from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import KarmaBadge, { tierFor } from "@/components/KarmaBadge";
import EditProfileCard from "@/components/EditProfileCard";
import FriendButton from "@/components/FriendButton";
import TrophyCase from "@/components/TrophyCase";
import UserClassBadge from "@/components/UserClassBadge";
import { useAuth } from "@/contexts/AuthContext";
import useSeo from "@/hooks/useSeo";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

function KarmaArrowIcon() {
  return <span className="font-bold">↑</span>;
}

function Stat({ label, value, icon: Icon, color, suffix }) {
  return (
    <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-5 relative overflow-hidden">
      <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full" style={{ background: `${color}22` }} />
      <div className="flex items-center gap-3 relative">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}33`, color }}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)]">{label}</div>
          <div className="font-display text-2xl font-bold">
            {value ?? 0}
            {suffix && <span className="text-sm text-[var(--tt-text-muted)] font-mono-tt ml-1">{suffix}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function PosterStrip({ title, icon: Icon, color, items, onOpen, emptyHint }) {
  return (
    <section className="mb-8">
      <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
        <Icon className="w-5 h-5" style={{ color }} />
        {title}
        {items.length > 0 && (
          <span className="text-xs font-mono-tt text-[var(--tt-text-muted)] font-normal">
            · {items.length}
          </span>
        )}
      </h2>
      {items.length === 0 ? (
        <div className="text-sm text-[var(--tt-text-muted)] py-3">{emptyHint}</div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
          {items.map((p) => (
            <button
              key={`${p.media_type}-${p.tmdb_id}`}
              type="button"
              data-testid={`profile-poster-${p.tmdb_id}`}
              onClick={() => onOpen(p)}
              className="group text-left"
              title={p.title}
            >
              <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-[var(--tt-surface)] border border-[var(--tt-border)] group-hover:border-[var(--tt-primary)] transition-colors">
                {p.poster ? (
                  <img
                    src={p.poster}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[10px] text-[var(--tt-text-muted)] p-1 text-center">
                    {p.title}
                  </div>
                )}
                {p.rating > 0 && (
                  <div className="absolute top-1.5 right-1.5 flex items-center gap-0.5 text-[9px] font-bold bg-black/70 backdrop-blur px-1 py-0.5 rounded">
                    <Star className="w-2.5 h-2.5 text-[var(--tt-yellow)] fill-current" />
                    {p.rating}
                  </div>
                )}
              </div>
              <div className="mt-1.5 text-[11px] font-bold truncate group-hover:text-[var(--tt-primary)] transition-colors">
                {p.title}
              </div>
              {p.year && (
                <div className="text-[10px] text-[var(--tt-text-muted)] font-mono-tt">{p.year}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

export default function ProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = React.useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const { data } = await api.get(`/users/${userId}/profile`);
      setProfile(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "User not found");
    } finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  // Per-user SEO metadata
  const profileName = profile?.name || "User";
  const profileBio = profile?.bio
    ? profile.bio.slice(0, 140)
    : `View ${profileName}'s TorHub profile — karma, weekly contributions, favorite movies and TV shows, watchlist, and comments.`;
  useSeo({
    title: profile ? `${profileName} (${profile.karma ?? 0} karma) — TorHub Profile` : "TorHub Profile",
    description: profileBio,
    path: `/u/${userId}`,
    image: profile?.picture,
    type: "profile",
  });

  const openItem = (p) => navigate(`/title/${p.media_type}/${p.tmdb_id}`);

  return (
    <div className="flex h-screen overflow-hidden bg-black text-white">
      <Sidebar />
      <div className="flex-1 h-full overflow-y-auto flex flex-col">
        <Header />
        <main className="px-4 md:px-8 py-6 flex-1 max-w-5xl mx-auto w-full">
          <Link to="/" className="text-[var(--tt-text-muted)] hover:text-white flex items-center gap-2 text-sm mb-6" data-testid="profile-back-home">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>

          {loading ? (
            <div className="text-[var(--tt-text-muted)]">Loading profile…</div>
          ) : err ? (
            <div className="text-amber-400">{err}</div>
          ) : profile ? (
            <>
              {currentUser && currentUser.user_id === profile.user_id && (
                <EditProfileCard profile={profile} onUpdated={load} />
              )}

              <section className="flex flex-col md:flex-row items-start md:items-center gap-5 mb-6" data-testid="profile-header">
                {profile.picture ? (
                  <img src={profile.picture} alt="" className="w-24 h-24 rounded-full object-cover border-2 border-[var(--tt-primary)]/40" />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[var(--tt-primary)] via-[var(--tt-yellow)] to-[var(--tt-purple)] flex items-center justify-center text-4xl font-bold">
                    {profile.name[0].toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="font-display text-3xl font-extrabold" data-testid="profile-name">{profile.name}</h1>
                    {profile.role === "admin" && (
                      <span className="flex items-center gap-1 text-[10px] uppercase font-bold bg-[var(--tt-yellow)] text-black px-2 py-0.5 rounded">
                        <ShieldCheck className="w-3 h-3" /> Admin
                      </span>
                    )}
                    {profile.is_banned && (
                      <span className="flex items-center gap-1 text-[10px] uppercase font-bold bg-red-500/20 text-red-400 px-2 py-0.5 rounded">
                        <Ban className="w-3 h-3" /> Banned
                      </span>
                    )}
                    <KarmaBadge karma={profile.karma} size="lg" />
                    {profile.hof_wins > 0 && (
                      <Link
                        to="/hall-of-fame"
                        data-testid="profile-hof-badge"
                        title="Hall of Fame wins"
                        className="flex items-center gap-1 text-[10px] uppercase font-bold bg-[var(--tt-yellow)]/15 text-[var(--tt-yellow)] border border-[var(--tt-yellow)]/40 px-2 py-0.5 rounded hover:bg-[var(--tt-yellow)]/25 transition-colors"
                      >
                        <Crown className="w-3 h-3" /> {profile.hof_wins}× Champion
                      </Link>
                    )}
                  </div>
                  {profile.bio && (
                    <p
                      data-testid="profile-bio"
                      className="text-sm text-[var(--tt-text)] mt-3 leading-relaxed max-w-2xl italic"
                    >
                      “{profile.bio}”
                    </p>
                  )}
                  <div className="text-sm text-[var(--tt-text-muted)] mt-3 flex items-center gap-4 flex-wrap">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      Joined {profile.joined?.slice(0, 10) || "—"}
                    </span>
                    {profile.karma_rank && (
                      <span className="flex items-center gap-1.5">
                        <Trophy className="w-3.5 h-3.5 text-[var(--tt-yellow)]" />
                        Rank <span className="font-bold text-[var(--tt-text)]">#{profile.karma_rank}</span> all-time
                      </span>
                    )}
                    {tierFor(profile.karma) && (
                      <span className="flex items-center gap-1.5">
                        Tier:
                        <span className="font-bold" style={{ color: tierFor(profile.karma).color }}>
                          {tierFor(profile.karma).label}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
                <div className="md:ml-auto md:self-start">
                  <FriendButton
                    userId={profile.user_id}
                    peerName={profile.name}
                    peerPicture={profile.picture}
                  />
                </div>
              </section>

              <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="profile-stats">
                <Stat label="Karma" value={profile.karma} icon={KarmaArrowIcon} color="#FF2A6D" />
                <Stat label="Comments" value={profile.comments} icon={MessageSquare} color="#9D4CDD" />
                <Stat label="Favorites" value={profile.favorites_count} icon={Heart} color="#F9A826" />
                <Stat label="Watchlist" value={profile.watchlist_count} icon={Bookmark} color="#22c55e" />
              </section>

              <div className="mb-8 grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-4">
                <UserClassBadge userId={profile.user_id} />
                <TrophyCase userId={profile.user_id} />
              </div>

              <PosterStrip
                title="Top Favorites"
                icon={Heart}
                color="#FF2A6D"
                items={profile.favorites || []}
                onOpen={openItem}
                emptyHint="No favorites yet."
              />

              <PosterStrip
                title="Watching Next"
                icon={Bookmark}
                color="#F9A826"
                items={profile.watchlist || []}
                onOpen={openItem}
                emptyHint="Watchlist is empty."
              />

              <section>
                <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[var(--tt-primary)]" />
                  Recent Activity
                </h2>
                {profile.recent_comments.length === 0 ? (
                  <div className="text-sm text-[var(--tt-text-muted)] py-6">No comments yet.</div>
                ) : (
                  <ul className="space-y-3" data-testid="profile-recent-comments">
                    {profile.recent_comments.map((c) => (
                      <li key={c.comment_id} className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-lg p-4">
                        <div className="text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)] mb-1">
                          {c.parent_id ? "Reply" : "Comment"} on a {c.media_type === "tv" ? "TV show" : "movie"} · {timeAgo(c.created_at)}
                        </div>
                        <p className="text-sm whitespace-pre-wrap break-words">{c.text}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          ) : null}
        </main>
        <Footer />
      </div>
    </div>
  );
}
